import { injectionKey } from '@shared/injection'
import type { OmniSearchEmptyState, OmniSearchResponse } from '@consumer/types/serializers'
import { defineStore } from 'pinia'
import type { Result } from './types'
import { goToSearch, isRecentSearch, navigateToResult } from './utils'

export type OmniSearchApi = ReturnType<typeof setupOmniSearch>
export const OMNI_SEARCH_INJECTION_KEY = injectionKey<OmniSearchApi>('omniSearch')
export const QUERIES_STORAGE_KEY = 'omniSearchQueries'

export const useQueryStore = defineStore(QUERIES_STORAGE_KEY, () => {
  const all = ref<string[]>([])

  function add (query: string) {
    if (!query) return
    if (all.value[0] === query) return // Avoid duplicates

    all.value.unshift(query)
    all.value = all.value.slice(0, 3) // Keep only the last 3 queries
  }

  function clear () {
    all.value = []
  }

  return {
    all,
    add,
    clear,
  }
}, {
  persist: {
    pick: ['all'],
    key: QUERIES_STORAGE_KEY,
  },
})

export function useOmniSearch ({ setup }: { setup?: boolean } = {}): OmniSearchApi {
  if (setup) {
    const api = setupOmniSearch()
    provide(OMNI_SEARCH_INJECTION_KEY, api)
    return api
  }
  else {
    const api = inject(OMNI_SEARCH_INJECTION_KEY)
    if (!api) {
      throw new Error('useOmniSearch must be called within a component that provides OmniSearchApi')
    }
    return api
  }
}

function setupOmniSearch () {
  let searchResponse = $ref<OmniSearchResponse>()
  let query = $ref<string>()
  let emptyState = $ref<OmniSearchEmptyState>()
  let open = $ref<boolean>(false)
  const trimmedQuery = $computed(() => query?.trim())
  const queryStore = useQueryStore()
  const showAllResultsValue = 'all' as Result
  const popularNearby = { search: { category: 'Popular', city: 'Nearby' } } as Result

  let selectedResult = $ref<Result>()

  function setSearchResponse (response?: OmniSearchResponse) {
    searchResponse = response
  }

  function setEmptyState (state?: OmniSearchEmptyState) {
    emptyState = state
  }

  function setSelectedResult (result: Result) {
    selectedResult = result
  }

  function reset () {
    selectedResult = undefined
    searchResponse = undefined
  }

  function setQuery (newQuery: string) {
    query = newQuery
  }

  function setOpen (isOpen: boolean) {
    open = isOpen
  }

  function selectResult (result?: Result) {
    if (!result)
      result = popularNearby

    selectedResult = result

    if (trimmedQuery) queryStore.add(trimmedQuery)

    if (isRecentSearch(result)) {
      setQuery(result.recentSearch.query)
    }
    else {
      if (result === showAllResultsValue) {
        if (searchResponse?.zipCity) {
          navigateToResult(searchResponse.city, searchResponse.zipCity)
        }
        else {
          goToSearch(undefined, trimmedQuery)
        }
      }
      else {
        navigateToResult(result, trimmedQuery)
      }
    }
  }

  return {
    selectedResult: $$(selectedResult),
    searchResponse: $$(searchResponse),
    emptyState: $$(emptyState),
    query: $$(query),
    open: $$(open),
    trimmedQuery: $$(trimmedQuery),
    setSearchResponse,
    reset,
    showAllResultsValue,
    selectResult,
    setEmptyState,
    setQuery,
    setOpen,
    popularNearby,
    setSelectedResult,
  }
}
